import { ApolloError, useMutation } from '@apollo/client'
import { SnackbarVariant } from '@focaldata/cin-ui-components'
import { useCallback } from 'react'
import {
  AudienceCriterionState,
  CustomQuotaInputInput,
  UpdateAudienceMemberV2Mutation,
  UpdateAudienceMemberV2MutationVariables
} from '../../data/gql-gen/questionnaire/graphql'
import { UPDATE_AUDIENCE_MEMBER_V2 } from '../../data/gql/questionnaire/mutations/updateAudienceMemberV2'
import { draftAudienceRefetchQuery } from '../../data/gql/questionnaire/queries/audiences'
import { customCriteriaRefetchQuery } from '../../data/gql/questionnaire/queries/customCriteria'
import { fieldworkAudienceRefetchQuery } from '../../data/gql/questionnaire/queries/fieldworkAudience'
import { getFeasibilityEstimateRefetchQuery } from '../../data/gql/questionnaire/queries/getFeasibilityEstimate'
import { LoggerErrorType } from '../../data/logger'
import { useSurveyLengthData } from '../../modules/Audience/AudienceQuestionCard/AudienceQuestionCard.hooks'
import { captureApolloError } from '../../utils/HelperFunctions'
import {
  GLOBAL_AUDIENCE_READ_ONLY_MESSAGE,
  isGlobalAudienceReadOnlyError
} from '../../utils/globalAudienceError'
import { useOptionalWaveId } from '../useOptionalWaveId'
import { useProjectId } from '../useProjectId'
import { useSafeEnqueueSnackbar } from '../useSafeEnqueueSnackbar'
import { useSurveyId } from '../useSurveyId'

export const useUpdateAudienceMemberV2 = () => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const { loi } = useSurveyLengthData()
  const enqueueSnackbar = useSafeEnqueueSnackbar()
  const [updateAudienceMemberV2Mutation] = useMutation<
    UpdateAudienceMemberV2Mutation,
    UpdateAudienceMemberV2MutationVariables
  >(UPDATE_AUDIENCE_MEMBER_V2, {
    context: { clientName: 'questionnaire' },
    onError(error) {
      captureApolloError(
        LoggerErrorType.ApolloMutation,
        'UPDATEAudienceMemberV2',
        error
      )
      // Backstop: the UI disables structural edits to a global question in a
      // non-base market, but surface the backend guard if one slips through.
      if (isGlobalAudienceReadOnlyError(error)) {
        enqueueSnackbar?.(GLOBAL_AUDIENCE_READ_ONLY_MESSAGE, {
          variant: SnackbarVariant.Error
        })
      }
    }
  })

  const updateAudienceMemberV2 = useCallback(
    (options: Parameters<typeof updateAudienceMemberV2Mutation>[0]) =>
      updateAudienceMemberV2Mutation({
        ...options,
        variables: options?.variables && {
          ...options.variables,
          input: { ...options.variables.input, waveId }
        }
      }),
    [updateAudienceMemberV2Mutation, waveId]
  )

  const updateAudienceMemberV2Settings = (
    memberId: string,
    newSettings: UpdateAudienceMemberV2MutationVariables['input']['settings']
  ) => {
    updateAudienceMemberV2({
      variables: {
        input: {
          projectId,
          surveyId,
          memberId,
          settings: newSettings
        }
      },
      refetchQueries: [
        draftAudienceRefetchQuery(projectId, surveyId, waveId),
        getFeasibilityEstimateRefetchQuery(projectId, surveyId, loi)
      ]
    })
  }

  const updateStandardMemberV2Quotas = (
    memberId: string,
    quotas: CustomQuotaInputInput[]
  ) => {
    updateAudienceMemberV2({
      variables: {
        input: {
          projectId,
          surveyId,
          memberId,
          updateStandardMember: {
            standardQuotas: quotas
          }
        }
      },
      refetchQueries: [
        draftAudienceRefetchQuery(projectId, surveyId, waveId),
        getFeasibilityEstimateRefetchQuery(projectId, surveyId, loi)
      ]
    }).catch((err) =>
      captureApolloError(
        LoggerErrorType.ApolloMutation,
        'updateAudienceMemberV2',
        err
      )
    )
  }

  /** Resolves false when the move was refused, so callers can roll back. */
  const updateAudienceMemberV2Position = async (
    memberId: string,
    position: number
  ): Promise<boolean> => {
    try {
      const result = await updateAudienceMemberV2({
        variables: {
          input: {
            projectId,
            surveyId,
            memberId,
            position
          }
        },
        refetchQueries: [
          draftAudienceRefetchQuery(projectId, surveyId, waveId),
          fieldworkAudienceRefetchQuery(surveyId),
          getFeasibilityEstimateRefetchQuery(projectId, surveyId, loi)
        ]
      })
      // `onError` above makes Apollo resolve a rejection as a single ApolloError
      // rather than reject, so an `errors.length` check would miss every failure.
      const errors: unknown = result.errors
      return Array.isArray(errors) ? errors.length === 0 : errors == null
    } catch (err) {
      captureApolloError(
        LoggerErrorType.ApolloMutation,
        'updateAudienceMemberV2',
        err as ApolloError
      )
      return false
    }
  }

  const toggleCustomAudienceMemberState = (
    memberId: string,
    enabled: boolean
  ) => {
    updateAudienceMemberV2({
      variables: {
        input: {
          projectId,
          surveyId,
          memberId,
          updateCustomMember: {
            state: enabled
              ? AudienceCriterionState.Enabled
              : AudienceCriterionState.Disabled
          }
        }
      },
      refetchQueries: [
        draftAudienceRefetchQuery(projectId, surveyId, waveId),
        fieldworkAudienceRefetchQuery(surveyId),
        customCriteriaRefetchQuery(projectId, surveyId, waveId)
      ]
    })
  }

  return {
    updateAudienceMemberV2,
    updateAudienceMemberV2Settings,
    updateStandardMemberV2Quotas,
    updateAudienceMemberV2Position,
    toggleCustomAudienceMemberState
  }
}
